import React, { useEffect, useMemo, useRef, useState } from "react";
import './OrderTicket.css';
import { generateBarcodeFromValue } from '../../components/barcodeHelper';

const formatAmount = (value) =>
  `${Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} DHS`;

const OrderTicket = () => {
  const order = JSON.parse(localStorage.getItem('orderTicket')) || {};
  const ticketSummary = order.ticketSummary || {};
  const pluTicket = order.pluTicket || null;
  const qrCodeRef = useRef(null);
  const [qrReady, setQrReady] = useState(!pluTicket);

  const lines = useMemo(() => {
    if (Array.isArray(order.lines) && order.lines.length) {
      return order.lines;
    }

    if (!Array.isArray(ticketSummary.items) || !ticketSummary.items.length) {
      return [];
    }

    return ticketSummary.items.map((item) => {
      const quantity = Number(item?.qty ?? item?.quantity ?? 0) || 0;
      const lineTotalCandidate =
        item?.total ??
        item?.amount ??
        (Number(item?.price) || 0) * quantity;
      const lineTotal = Number.isFinite(Number(lineTotalCandidate))
        ? Number(lineTotalCandidate)
        : 0;
      const description =
        item?.label ||
        item?.name ||
        item?.ref ||
        item?.product_ref ||
        'Produit';
      const barcodeValue =
        item?.barcode ||
        item?.ref ||
        item?.product_ref ||
        item?.fk_product ||
        '';

      return {
        quantity,
        description,
        price: lineTotal,
        barcodeSvg: generateBarcodeFromValue(barcodeValue),
      };
    });
  }, [order.lines, ticketSummary]);

  const total = useMemo(() => {
    const parseNumber = (value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };

    const candidateTotals = [
      parseNumber(order.total),
      parseNumber(ticketSummary.total),
      parseNumber(ticketSummary.amount),
    ];

    const resolvedCandidate = candidateTotals.find(
      (value) => value !== null
    );

    if (resolvedCandidate !== undefined && resolvedCandidate !== null) {
      return resolvedCandidate;
    }

    return lines.reduce((sum, line) => sum + (Number(line.price) || 0), 0);
  }, [lines, order.total, ticketSummary]);

  useEffect(() => {
    if (!pluTicket?.qrValue) {
      setQrReady(true);
      return undefined;
    }

    let cancelled = false;

    const renderQrCode = () => {
      if (cancelled || !qrCodeRef.current || !window.QRCode) {
        return;
      }

      qrCodeRef.current.innerHTML = '';
      new window.QRCode(qrCodeRef.current, {
        text: pluTicket.qrValue,
        width: 148,
        height: 148,
        correctLevel: window.QRCode.CorrectLevel.H,
      });
      setQrReady(true);
    };

    if (window.QRCode) {
      renderQrCode();
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js';
    script.async = true;
    script.onload = renderQrCode;
    script.onerror = () => {
      setQrReady(true);
    };
    document.body.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [pluTicket]);

  useEffect(() => {
    if (!qrReady) {
      return undefined;
    }

    const printTimeout = window.setTimeout(() => {
      window.print();
    }, 150);

    return () => {
      window.clearTimeout(printTimeout);
    };
  }, [qrReady]);

  return (
    <div className="order-ticket">
      <div className="ticket-header">
        <h2 className="ticket-title">Commande Le celadon</h2>
        {order.storeName ? (
          <div className="ticket-store">{order.storeName}</div>
        ) : null}
        {order.orderDateTime ? (
          <div className="ticket-date">{order.orderDateTime}</div>
        ) : null}
      </div>

      <table className="ticket-lines">
        <tbody>
          {lines.map((line, idx) => (
            <tr key={idx}>
              <td>
                <div className="ticket-line-description">
                  {line.quantity} x {line.description}
                </div>
              </td>
              <td className="ticket-line-price">{formatAmount(line.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr className="ticket-divider" />

      <div className="ticket-total">
        <span>Total</span>
        <strong>{formatAmount(total)}</strong>
      </div>

      {pluTicket?.qrValue ? (
        <div className="plu-ticket-container">
          <div ref={qrCodeRef} className="plu-ticket-qrcode" />
        </div>
      ) : (
        lines.map((line, idx) =>
          Array.from({ length: line.quantity }).map((_, i) => (
            <div
              key={`${idx}-${i}`}
              className="barcode-container"
              dangerouslySetInnerHTML={{ __html: line.barcodeSvg }}
            />
          ))
        )
      )}
    </div>
  );
};

export default OrderTicket;
